/**
 * The `countryFormat` account setting — SIMS-9094.
 *
 * The setting's value is NOT stored consistently. Most accounts hold a real
 * object; others hold the same payload as a JSON **string**:
 *
 *   "{\"dateFormat\":\"YYYY/MM/DD\",\"phoneDefaultCountry\":\"CA\",...}"
 *
 * Every consumer used to assume the object shape — `countryFormat?.dateFormat`,
 * or destructuring `const { phoneDefaultCountry } = countryFormat ?? DEFAULT`.
 * Reading a property off a string yields `undefined`, so on a string-valued
 * account every one of them silently fell back to its US/`MM/DD/YYYY` default:
 * the phone flag, the date format, and the address field labels alike. That is
 * why Register Participant appeared to "already honour the setting" — it does,
 * but only for the accounts that store an object.
 *
 * Parse through here rather than reading the raw setting, so both shapes work
 * and a malformed string degrades to the caller's default instead of throwing.
 */

export interface CountryFormatAddressField {
	name: string;
	label: string;
	width?: number;
}

export interface CountryFormat {
	dateFormat?: string;
	phoneDefaultCountry?: string;
	addressFields?: CountryFormatAddressField[];
}

/** Shared fallback, matching the setting catalog's own default. */
export const DEFAULT_COUNTRY_FORMAT: Required<
	Pick<CountryFormat, "dateFormat" | "phoneDefaultCountry">
> & {
	addressFields: CountryFormatAddressField[];
} = {
	dateFormat: "MM/DD/YYYY",
	phoneDefaultCountry: "US",
	addressFields: [
		{ name: "addressLine1", label: "Address Line 1", width: 12 },
		{ name: "addressLine2", label: "Address Line 2", width: 12 },
		{ name: "city", label: "City", width: 4 },
		{ name: "state", label: "State", width: 4 },
		{ name: "zipCode", label: "ZIP Code", width: 4 },
	],
};

/**
 * Normalise the raw setting value into an object.
 *
 * Returns `undefined` when the value is absent, is a non-object JSON scalar, or
 * is a string that does not parse — all cases where the caller should use its
 * own default rather than a half-built object.
 */
export const parseCountryFormat = (raw: unknown): CountryFormat | undefined => {
	if (!raw) return undefined;

	if (typeof raw === "string") {
		try {
			const parsed: unknown = JSON.parse(raw);
			return parsed && typeof parsed === "object" && !Array.isArray(parsed)
				? (parsed as CountryFormat)
				: undefined;
		} catch {
			// A malformed stored string is a data problem, not a render error:
			// fall back so the form still opens.
			return undefined;
		}
	}

	if (typeof raw === "object" && !Array.isArray(raw)) {
		return raw as CountryFormat;
	}

	return undefined;
};
